import { Component, computed, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { of } from 'rxjs';
import { MceApiService, PrimaryMethodSelectDto, SecondaryMethodSelectDto } from '../../../../core/mceapi.client.generated';
import { AmbientTemperatureComponent } from '../ambient-temperature/ambient-temperature.component';
import { BuriedComponent } from '../buried/buried.component';
import { GroupingComponent } from '../grouping/grouping.component';
import { UserDefinedFactorComponent } from '../user-defined-factor/user-defined-factor.component';
import { FactorsBS7671 } from './factors-bs7671.model';
import { BS7671_STANDARD_GUID } from '../../../../shared/constants';
import { SelectedCable } from '../../selection/cable-selection.model';
import { SystemVoltage, createSystemVoltageDto } from '../../system-voltage/system-voltage.model';
import { appOpt } from '../../../../core/mceapi.client.helpers';
import { isSome, match, none, Option } from '../../../../shared/functions/functional';
import { FormImage, FormImageComponent } from '../../../../shared/components/form-image/form-image.component';
import { EditCable } from '../../cable-edit.model';

@Component({
  selector: 'app-factors-bs7671',
  imports: [FormField, MatSelectModule, MatInputModule, UserDefinedFactorComponent,
    AmbientTemperatureComponent, GroupingComponent, BuriedComponent, FormImageComponent],
  templateUrl: './factors-bs7671.component.html',
  styles: ``,
})
export class FactorsBs7671Component {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);

  primaryMethodsRx = rxResource({
    params: () => ({
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      systemVoltageValid: this.form().systemVoltage().valid()
    }),
    stream: ({ params }) =>
      params.libraryGuid !== '' && params.systemVoltageValid
        ? this.mceApiService.v1CableInstallationMethodsPrimary({
          libraryGuid: params.libraryGuid,
          systemVoltage: params.systemVoltage,
          standardGuid: BS7671_STANDARD_GUID
        })
        : of([] as PrimaryMethodSelectDto[])
  });
  primaryMethods = computed(() => this.primaryMethodsRx.value() ?? []);

  secondaryMethodsRx = rxResource({
    params: () => ({
      libraryGuid: this.form().selectedCable.libraryGuid().value(),
      primaryGuid: this.form().installation.bs7671Factors.primaryMethodGuid().value(),
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      systemVoltageValid: this.form().systemVoltage().valid()
    }),
    stream: ({ params }) =>
      params.libraryGuid !== '' && params.primaryGuid !== '' && params.systemVoltageValid
        ? this.mceApiService.v1CableInstallationMethodsSecondary({
          libraryGuid: params.libraryGuid,
          systemVoltage: params.systemVoltage,
          primaryGuid: params.primaryGuid,
          standardGuid: BS7671_STANDARD_GUID
        })
        : of([] as SecondaryMethodSelectDto[])
  });
  secondaryMethods = computed(() => this.secondaryMethodsRx.value() ?? []);

  isBuriedRx = rxResource({
    params: () => ({ secondaryMethod: this.form().installation.bs7671Factors.secondaryMethodGuid().value() }),
    stream: ({ params }) =>
      params.secondaryMethod !== ''
        ? this.mceApiService.v1CableInstallationMethodsIsBuried(params.secondaryMethod)
        : of({ value: false })
  });
  isBuried = computed(() => this.isBuriedRx.value()?.value ?? false);
  setModelIsBuried = effect(() => {
    this.form().installation.bs7671Factors.isBuried().value.set(this.isBuried());
  });

  isThermallyInsulatedWallRx = rxResource({
    params: () => ({ secondaryMethod: this.form().installation.bs7671Factors.secondaryMethodGuid().value() }),
    stream: ({ params }) =>
      params.secondaryMethod !== ''
        ? this.mceApiService.v1CableInstallationMethodsBS7671IsThermallyInsulatedWall(params.secondaryMethod)
        : of({ value: false })
  });
  isThermallyInsulatedWall = computed(() => this.isThermallyInsulatedWallRx.value()?.value ?? false);
  installationImage = computed<Option<string>>(() => {
    let imageUrl = this.secondaryMethods().find(m => m.value === this.form().installation.bs7671Factors.secondaryMethodGuid().value())?.imageUrl;
    return imageUrl
      ? appOpt(imageUrl)
      : none;
  });


  resetPrimaryMethod = effect(() => {
    const primaryMethodGuid = this.form().installation.bs7671Factors.primaryMethodGuid().value() ?? '';
    const primaryMethods = this.primaryMethods();

    if (this.primaryMethodsRx.isLoading() ||  primaryMethods.length === 0) return;
    if (!primaryMethods.some(m => m.value === primaryMethodGuid)) {
      const firstPrimaryMethodGuid = primaryMethods[0].value;
      if (firstPrimaryMethodGuid !== undefined) {
        this.form().installation.bs7671Factors.primaryMethodGuid().value.set(firstPrimaryMethodGuid);
      }
    }
  });

  resetSecondaryMethod = effect(() => {
    const secondaryMethodGuid = this.form().installation.bs7671Factors.secondaryMethodGuid().value() ?? '';
    const secondaryMethods = this.secondaryMethods();

    if (this.secondaryMethodsRx.isLoading() ||  secondaryMethods.length === 0) return;
    if (!secondaryMethods.some(m => m.value === secondaryMethodGuid)) {
      const firstSecondaryMethodGuid = secondaryMethods[0].value;
      if (firstSecondaryMethodGuid !== undefined) {
        this.form().installation.bs7671Factors.secondaryMethodGuid().value.set(firstSecondaryMethodGuid);
      }
    }
  });

  resetBuried = effect(() => {
    const isBuried = this.isBuried();
    if (!isBuried || !this.form().installation.bs7671Factors.secondaryMethodGuid().dirty() || this.form().installation.bs7671Factors.isBuried().value() === isBuried) return;
    this.form().installation.bs7671Factors.isBuried().value.set(isBuried);
  });

  setModelIsThermallyInsulatedWall = effect(() => {
    if (this.isThermallyInsulatedWallRx.isLoading() || !this.isThermallyInsulatedWallRx.hasValue()) return;
    this.form().installation.bs7671Factors.isThermallyInsulatedWall().value.set(this.isThermallyInsulatedWall());
  });



  viewState = computed(() => ({
    hasInstallationImage: isSome(this.installationImage()),
    installationImage: ({
      url: match(() => '', (x) => x)(this.installationImage()),
      alt: 'Installation image',
      options: {
        height: 130,
        width: 260
      }
    } as FormImage)
  }));

  ngOnDestroy() {

    if (!this.form().installation.bs7671Factors.thermalInsulationThickness().valid())
      this.form().installation.bs7671Factors.thermalInsulationThickness().value.set(0);

  }

}
